import { useEffect } from 'react';
import { useRoute } from '@react-navigation/native';

import { useChatsModel } from '@models/Chats/ChatsModel';
import { ChatNavigatorRoutePropType } from '@navigation';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { useAsyncInit, useMessagesSocketListeners } from '@hooks';

type RoutePropType = ChatNavigatorRoutePropType<'Messaging'>;

export function useMessaging() {
  const { params } = useRoute<RoutePropType>();
  const chats = useChatsModel();
  const conversation = chats.getById(params.conversationId);

  useMessagesSocketListeners(
    {
      onReceive: ({ conversationId, ...message }) => {
        if (conversationId === conversation?.id) {
          conversation.addNewMessage(message);
        }
      },
    },
    [conversation],
  );

  useAsyncInit(async () => {
    if (!conversation) {
      try {
        await chats.fetchById.run(params.conversationId);
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }
  }, [conversation]);

  useAsyncInit(async () => {
    try {
      await conversation?.messages.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }, [conversation]);

  useEffect(() => {
    return () => {
      markAsRead();
    };
  }, [conversation]);

  async function markAsRead() {
    if (!conversation?.hasNewMessages) {
      return;
    }

    try {
      await conversation.markAsRead.run();
    } catch (error) {
      // Do nothing
    }
  }

  async function fetchMoreMessages() {
    try {
      await conversation?.messages.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  return { conversation, fetchMoreMessages };
}
